import '../sharpen/sharpen-legal-disclaimer';
import { BaseComponent } from '../base-component/base-component';
import { ItemType } from './product.interface';
import { booleanConverter } from '../../common/converters';
import { customElement, property } from 'lit/decorators.js';
import { getAleksSharpenVerbiage, getSharpenVerbiage } from '../sharpen/utils/sharpen.util';
import { getVirtualLabsVerbiage } from '../virtual-labs/utils/virtual-labs.util';
import { html, nothing } from 'lit';

/**
 * A static element to display product information.
 */
@customElement('mhecomm-product-verbiage')
export class ProductVerbiage extends BaseComponent {
  /**
   * The standalone application name.
   */
  @property()
  application?: string;

  @property()
  analyticsApplication?: string;

  @property()
  analyticsOrderType?: string;

  /**
   * The flow name.
   */
  @property()
  flow?: string;

  /**
   * Connect only.
   */
  @property({ converter: booleanConverter })
  looseLeafAvailable?: boolean;

  /**
   * Connect only.
   */
  @property({ converter: booleanConverter })
  hasEbook?: boolean;

  @property({ converter: booleanConverter })
  isConnectSharpenBundle?: boolean;

  @property({ converter: booleanConverter })
  isOrderConfirmation?: boolean;

  @property({ converter: booleanConverter })
  isSharpenCompanion?: boolean;

  @property({ converter: booleanConverter })
  isSharpenAdvantage?: boolean;

  @property({ converter: booleanConverter })
  isMhGoItem?: boolean;

  @property({ converter: booleanConverter })
  isVirtualLabs?: boolean;

  @property({ converter: booleanConverter })
  isAleksSharpenCompanionBundle?: boolean;

  renderComp() {
    return html`
      ${this.shouldShowSharpenVerbiage() ? this.getSharpenVerbiage() : nothing}
      ${this.isVirtualLabs ? getVirtualLabsVerbiage() : nothing}
      <mhecomm-sharpen-legal-disclaimer
        .isConnectSharpenBundle=${this.isConnectSharpenBundle}
        .isOrderConfirmation=${this.isOrderConfirmation}
        .isSharpenCompanion=${this.isSharpenCompanion}
        .isSharpenAdvantage=${this.isSharpenAdvantage}
        .isMhGoItem=${this.isMhGoItem}
        .isAleksSharpenCompanionBundle=${this.isAleksSharpenCompanionBundle}
        .analyticsApplication=${this.analyticsApplication}
        .analyticsOrderType=${this.analyticsOrderType}
      >
      </mhecomm-sharpen-legal-disclaimer>
    `;
  }

  /**
   * Displays product verbiage for Connect and MH GO.
   *
   * On MH GO, it will render only if the product is Sharpen companion.
   *
   * @private
   */
  private shouldShowSharpenVerbiage(): boolean {
    return !!(this.application === 'connect' || (this.flow === ItemType.mhGo && this.isSharpenCompanion) || this.isAleksSharpenCompanionBundle);
  }

  /**
   * Returns a different verbiage for Sharpen companion or Connect + Sharpen bundle.
   *
   * @private
   */
  private getSharpenVerbiage() {
    if (this.isConnectSharpenBundle || (this.isSharpenCompanion && !this.isAleksSharpenCompanionBundle) || this.isSharpenAdvantage) {
      return getSharpenVerbiage(this.looseLeafAvailable);
    }

    if (this.isAleksSharpenCompanionBundle) {
      return getAleksSharpenVerbiage();
    }

    return html`
      <div class="rounded-1 mb-2">
        <h3 class="fw-bold fs-6 p-0 m-0">What's included:</h3>
        <ul id="product-info-verbiage-content" data-automation-id="product-info-verbiage-content">
          ${this.hasEbook ? html` <li>Access to the eBook</li>` : nothing}
          <li>Online homework and quizzes</li>
          <li>Interactive tools and resources</li>
          <li>Free ReadAnywhere® app to access your eBook and Smartbook assignments anywhere</li>
          ${this.looseLeafAvailable
            ? html` <li id="product-info-verbiage-loose-leaf" data-automation-id="product-info-verbiage-loose-leaf">
                Exclusive discounts on a loose-leaf printed textbook in a three-ring binder
              </li>`
            : nothing}
        </ul>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-product-verbiage': ProductVerbiage;
  }
}
